// TRAC-1124: tell the page around /embed when the chat's text box gains or loses focus.
//
// On an iPhone the loader pins the full-screen chat to the part of the screen above
// the keyboard. When the keyboard closes, iOS moves the page back at once but keeps
// reporting the old visible area, with no event, until its close animation ends, so
// the loader only learnt about the close half a second late and the site showed above
// the chat in between. Focus leaving the text box is the earliest sign of that close,
// and only this frame can see it: the chat is a cross-origin iframe on tirerack.com.
//
// Focus moving from one of our fields to another (the message box to a survey
// comment, say) keeps the keyboard up, so it is not reported as a loss.
//
// Safe to spoof: the loader acts on it only from this frame's own window
// (e.source === frame.contentWindow), and the worst a forged one could do is size the
// panel to the full screen until the next viewport change.

import { PROD_PARENT_ORIGINS } from '../utils/parentOrigin';
import { PHONE_MEDIA_QUERY } from '../utils/phoneKeyboard';

export const KB_FOCUS_MESSAGE = 'TRACI_KB_FOCUS';

const NON_TEXT_INPUTS = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

/** True for an element that brings up the on-screen keyboard. */
export function isTextField(el: EventTarget | null): boolean {
  const e = el as HTMLElement | null;
  if (!e || typeof e.tagName !== 'string') return false;
  if (e.tagName === 'TEXTAREA') return true;
  if (e.tagName === 'INPUT') return !NON_TEXT_INPUTS.has(((e as HTMLInputElement).type || 'text').toLowerCase());
  return e.isContentEditable === true;
}

/** Report text-field focus on `win` to its parent page; returns the uninstaller. */
export function installEmbedKeyboardBridge(win: Window, targetOrigin: string): () => void {
  if (!win.parent || win.parent === win) return () => {};
  const post = (focused: boolean): void => {
    try {
      win.parent.postMessage({ type: KB_FOCUS_MESSAGE, focused, source: 'AI-Assistant' }, targetOrigin);
    } catch { /* parent gone or sandboxed: the loader falls back to the viewport events */ }
  };
  const onIn = (e: FocusEvent): void => { if (isTextField(e.target)) post(true); };
  const onOut = (e: FocusEvent): void => { if (isTextField(e.target) && !isTextField(e.relatedTarget)) post(false); };
  win.document.addEventListener('focusin', onIn);
  win.document.addEventListener('focusout', onOut);
  return () => {
    win.document.removeEventListener('focusin', onIn);
    win.document.removeEventListener('focusout', onOut);
  };
}

// And the other way: the page around /embed tells the chat when the phone keyboard is
// actually on screen (TRACI_KB_VISIBLE, from the loader, which measures it from the
// visible area). The chat marks <html data-traci-kb="up"> for as long as it is, and
// its CSS flattens the message box (no drop shadow) only then. Focus alone was not
// enough: the box is focused when the chat opens, and Android's back button closes
// the keyboard but keeps the focus. Only this frame's parent is listened to, and the
// worst a forged message could do is take the box's drop shadow away.

export const KB_VISIBLE_MESSAGE = 'TRACI_KB_VISIBLE';
export const KB_ATTR = 'data-traci-kb';

// TRAC-1132: on tirerack.com the site's own chat window stays full screen when the
// phone keyboard opens, and the keyboard slides over the bottom of it. The site
// tells us how many px of the window's bottom the keyboard (and the browser bars
// above it) cover, on open, while it animates and on close (TRACI_KB_COVER,
// { cover }). The chat ends that far above the window's bottom, so its message box
// sits on the keyboard and nothing of the website shows between them. 0 or no
// message: the chat fills the window as before.
export const KB_COVER_MESSAGE = 'TRACI_KB_COVER';
export const KB_COVER_VAR = '--traci-kb-cover';
const KB_UP_MIN_PX = 80;   // less than this is a browser bar moving, not a keyboard

// TRAC-1152: the cover moves the chat, so it is taken only from the page that holds this
// frame AND from a Tire Rack origin (prod: the exact parent allowlist; dev and QA: any https
// tirerack.com host, plus this Mac's mock site by any of its own addresses: loopback, the Android
// emulator's 10.0.2.2, and a private Wi-Fi address such as 192.168.0.18 that a phone on the same
// network opens). Without the Wi-Fi address the phone try link dropped every report, so the box
// stayed under the iPhone keyboard.
// The sender's own origin (e.origin) is checked, so a browser without
// location.ancestorOrigins no longer skips the check. The value stays clamped to 0..innerHeight.
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/** A private IPv4 address (10/8, 172.16/12, 192.168/16): a machine on the local network, never a public site. */
function isPrivateIpv4(host: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if (m.slice(1).some((x) => Number(x) > 255)) return false;
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

/** True when a TRACI_KB_COVER from `origin` may move the chat in this environment. */
export function isAllowedCoverOrigin(origin: string, env: string): boolean {
  if (env === 'prod') return PROD_PARENT_ORIGINS.includes(origin);
  let u: URL;
  try { u = new URL(origin); } catch { return false; }
  if (u.protocol === 'https:' && (u.hostname === 'tirerack.com' || u.hostname.endsWith('.tirerack.com'))) return true;
  return (u.protocol === 'http:' || u.protocol === 'https:') && (LOCAL_HOSTS.has(u.hostname) || isPrivateIpv4(u.hostname));
}

/** Frames whose page has sent at least one accepted cover: only those pages follow the keyboard for us. */
const coverHeard = new WeakSet<Window>();

/**
 * Mirror the parent's keyboard reports onto `<html data-traci-kb="up">`; returns the uninstaller.
 * `ride` (TRAC-1152, embedKeyboardRide.ts) takes the cover over when it is installed: it moves the
 * message box on transforms and owns the cover variable and the attribute while it runs.
 */
export function installEmbedKeyboardState(win: Window, env: string, ride?: () => { cover(px: number): void } | null): () => void {
  if (!win.parent || win.parent === win) return () => {};
  const root = win.document.documentElement;
  const onMessage = (e: MessageEvent): void => {
    if (e.source !== win.parent) return;
    const d = e.data as { type?: unknown; up?: unknown; cover?: unknown } | null;
    if (d && d.type === KB_COVER_MESSAGE) {
      if (!isAllowedCoverOrigin(e.origin, env)) return;
      coverHeard.add(win);
      const px = typeof d.cover === 'number' && Number.isFinite(d.cover) ? Math.min(Math.max(0, Math.round(d.cover)), win.innerHeight || 0) : 0;
      const r = ride?.();
      if (r) { r.cover(px); return; }
      if (px > 0) root.style.setProperty(KB_COVER_VAR, px + 'px');
      else root.style.removeProperty(KB_COVER_VAR);
      if (px >= KB_UP_MIN_PX) root.setAttribute(KB_ATTR, 'up');
      else root.removeAttribute(KB_ATTR);
      return;
    }
    if (!d || d.type !== KB_VISIBLE_MESSAGE) return;
    if (d.up === true) root.setAttribute(KB_ATTR, 'up');
    else root.removeAttribute(KB_ATTR);
  };
  win.addEventListener('message', onMessage);
  return () => {
    win.removeEventListener('message', onMessage);
    root.removeAttribute(KB_ATTR);
    root.style.removeProperty(KB_COVER_VAR);
  };
}

// TRAC-1152: tap focus without the page slide. A tap on the unfocused message box makes iOS
// slide the whole page up to show it (visualViewport jumps 387 pt in one frame while the slide
// takes about 250 ms), and the site's counter-move always lands late: the chat window drops a
// keyboard's height and slides back. The chat focuses the box itself inside the tap, with
// preventScroll, so the keyboard opens with no slide. Left native: drags, multi-touch, a long
// press (the paste menu), a tap on the box while it already has focus, and any tap where our
// focus does not take (the browser's own then runs). The caret goes to the end of a draft.
// Phones in a frame only. Focus that does not come through a tap (VoiceOver, Switch Control, a
// hardware keyboard) keeps the browser's behaviour and the site's follow as the safety net.
// Only once the page has sent a cover we accepted (a fixed site sends 0 when the chat says it is
// ready): until then nothing would lift the box, so the browser's own focus and page slide run
// (a site without the cover, an origin we refuse, an old cached loader).
const TAP_SLOP_PX = 10;
const LONG_PRESS_MS = 450;

/** Focus the tapped message box ourselves (no page slide); returns the uninstaller. */
export function installEmbedTapFocus(win: Window, siteFollows: () => boolean = () => coverHeard.has(win)): () => void {
  if (!win.parent || win.parent === win || typeof win.matchMedia !== 'function' || !win.matchMedia(PHONE_MEDIA_QUERY).matches) return () => {};
  const doc = win.document;
  let start: { ta: HTMLTextAreaElement; x: number; y: number; at: number } | null = null;
  const onStart = (e: TouchEvent): void => {
    const ta = (e.target as Element | null)?.closest?.('textarea') as HTMLTextAreaElement | null;
    start = ta && doc.activeElement !== ta && e.touches.length === 1
      ? { ta, x: e.touches[0].clientX, y: e.touches[0].clientY, at: Date.now() } : null;
  };
  const onCancel = (): void => { start = null; };
  const onEnd = (e: TouchEvent): void => {
    const s = start; start = null;
    if (!s || !e.changedTouches.length || e.touches.length) return;
    const t = e.changedTouches[0];
    if (Math.abs(t.clientX - s.x) > TAP_SLOP_PX || Math.abs(t.clientY - s.y) > TAP_SLOP_PX) return;   // a drag
    if (Date.now() - s.at > LONG_PRESS_MS) return;                                                   // a long press
    if (!siteFollows()) return;                                                                     // nothing would lift the box
    s.ta.focus({ preventScroll: true });     // still inside the tap, so iOS opens the keyboard
    if (doc.activeElement !== s.ta) return;  // did not take: the browser's own tap focus runs
    e.preventDefault();                      // no native focus (that one slides the page)
    const n = s.ta.value.length;
    try { s.ta.setSelectionRange(n, n); } catch { /* not a text type that has a caret */ }
  };
  doc.addEventListener('touchstart', onStart, { passive: true, capture: true });
  doc.addEventListener('touchcancel', onCancel, { passive: true, capture: true });
  doc.addEventListener('touchend', onEnd, { passive: false, capture: true });
  return () => {
    doc.removeEventListener('touchstart', onStart, { capture: true });
    doc.removeEventListener('touchcancel', onCancel, { capture: true });
    doc.removeEventListener('touchend', onEnd, { capture: true });
  };
}
